import * as React from 'react';
import { styled } from '@linaria/react';

import useTickSfx from '@/hooks/use-tick-sfx';

import Demo, { DemoSliderControl } from '@/components/Demo';
import Space from '@/components/Space';

import {
  POSITION_RADIUS,
  Svg,
  Label,
  PositionDot,
  CodeSnippet,
  Row,
  Indicators,
} from './BasicShapes.shared';
import GuideLine from './GuideLine';
import SizeLine from './SizeLine';

const SIZE = 300;

function BasicRectangleDemo() {
  const playTick = useTickSfx();

  return (
    <Demo
      id="basic-rectangle-demo"
      outputWrapper={OutputWrapper}
      initialValues={{
        x: 60,
        y: 100,
        width: 180,
        height: 100,
      }}
      CustomGridComponent={ControlsGrid}
      controls={[
        <DemoSliderControl
          highlightControlWhenActive
          key="x"
          id="x"
          label="x"
          min={0}
          max={SIZE}
          step={10}
          wrapperStyle={{
            gridArea: 'x',
          }}
          onChange={() => {
            playTick();
          }}
        />,
        <DemoSliderControl
          highlightControlWhenActive
          key="y"
          id="y"
          label="y"
          min={0}
          max={SIZE}
          step={10}
          wrapperStyle={{
            gridArea: 'y',
          }}
          onChange={() => {
            playTick();
          }}
        />,
        <DemoSliderControl
          highlightControlWhenActive
          key="width"
          id="width"
          label="width"
          min={0}
          max={SIZE}
          wrapperStyle={{
            gridArea: 'width',
          }}
          onChange={() => {
            playTick();
          }}
        />,
        <DemoSliderControl
          highlightControlWhenActive
          key="height"
          id="height"
          label="height"
          min={0}
          max={SIZE}
          wrapperStyle={{
            gridArea: 'height',
          }}
          onChange={() => {
            playTick();
          }}
        />,
      ]}
    >
      {({ x, y, width, height }, activeControlId) => {
        const code = `\
<svg width="${SIZE}" height="${SIZE}">
  <rect
    x="${x}"
    y="${y}"
    width="${width}"
    height="${height}"
    fill="none"
    stroke="oklch(0.9 0.3 164)"
    stroke-width="5"
  />
</svg>\
`;

        return (
          <StyledRow>
            <StyledCodeSnippet code={code} />

            <SvgWrapper>
              <Svg size={SIZE}>
                {/* MAIN SHAPE: */}
                <rect
                  x={x}
                  y={y}
                  width={width}
                  height={height}
                  fill="transparent"
                  stroke="var(--wham-complementary-500)"
                  strokeWidth={5}
                />

                <Indicators
                  style={{
                    opacity: width === 0 || height === 0 ? 0 : 1,
                  }}
                >
                  <PositionDot cx={x} cy={y} />

                  {/* Guidelines — visible white lines */}
                  {/* x line */}
                  <GuideLine
                    x1={x - POSITION_RADIUS - 2}
                    y1={y}
                    x2={0}
                    y2={y}
                    data-is-active={activeControlId === 'x'}
                  />
                  {/* y line */}
                  <GuideLine
                    x1={x}
                    y1={y - POSITION_RADIUS - 2}
                    x2={x}
                    y2={0}
                    data-is-active={activeControlId === 'y'}
                  />
                  {/* width line */}
                  <SizeLine
                    x1={x}
                    y1={y + height + 12}
                    x2={x + width}
                    y2={y + height + 12}
                    isActive={activeControlId === 'width'}
                    isHidden={width <= 13 || height <= 0}
                  />
                  {/* height line */}
                  <SizeLine
                    x1={x + width + 12}
                    y1={y}
                    x2={x + width + 12}
                    y2={y + height}
                    isActive={activeControlId === 'height'}
                    isHidden={height <= 13 || width <= 0}
                  />

                  <Label
                    x={x / 2}
                    y={y + 20}
                    textAnchor="middle"
                    dominantBaseline="bottom"
                    data-is-hidden={x < 25}
                    data-is-active={activeControlId === 'x'}
                  >
                    x
                  </Label>
                  <Label
                    x={x + 30}
                    y={y / 2}
                    textAnchor="end"
                    dominantBaseline="middle"
                    data-is-hidden={y < 25}
                    data-is-active={activeControlId === 'y'}
                  >
                    y
                  </Label>
                  <Label
                    x={x + width / 2}
                    y={y + height + 32}
                    textAnchor="middle"
                    dominantBaseline="bottom"
                    data-is-hidden={width <= 40 || height <= 0}
                    data-is-active={activeControlId === 'width'}
                    style={{
                      fontSize: '0.85rem',
                    }}
                  >
                    width
                  </Label>
                  <Label
                    x={x + width + 29}
                    y={y + height / 2}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    data-is-active={activeControlId === 'height'}
                    data-is-hidden={height <= 40 || width <= 0}
                    transform={`rotate(90 ${x + width + 29} ${
                      y + height / 2
                    })`}
                    style={{
                      fontSize: '0.85rem',
                    }}
                  >
                    height
                  </Label>
                </Indicators>
              </Svg>
            </SvgWrapper>
          </StyledRow>
        );
      }}
    </Demo>
  );
}

const BREAKPOINT = '35rem';

const OutputWrapper = styled(Space)`
  container-type: inline-size;
  background: var(--color-background);
`;

const ControlsGrid = styled.div`
  display: grid;
  gap: 16px 32px;
  grid-template-areas:
    'x'
    'y'
    'width'
    'height';

  @media (min-width: 30rem) {
    grid-template-areas:
      'x y'
      'width height';
  }
`;

const StyledRow = styled(Row)`
  @container (max-width: ${BREAKPOINT}) {
    flex-direction: column;
    align-items: center;
  }
`;

const SvgWrapper = styled.div`
  display: flex;

  @container (max-width: ${BREAKPOINT}) {
    position: relative;
  }
`;

const StyledCodeSnippet = styled(CodeSnippet)`
  @container (max-width: ${BREAKPOINT}) {
    width: 100%;
  }
`;

export default BasicRectangleDemo;
